import { Controller } from 'react-hook-form';
import Button from 'src/components/Button';
import DatePicker from 'src/components/date-picker/DatePicker';
import Modal, { ModalFooter } from 'src/components/Modal';
import SelectField from 'src/components/SelectField';
import TextAreaField from 'src/components/TextAreaField';
import { useStudentActivitiesQuery } from 'src/dictionaries/dictionariesApi';

import { generateDurationOptions } from './Activities.utils';
import { useActivityForm } from './ActivityForm.hook';
import { ActivityFormData } from './ActivityForm.schema';
import { useActivityDatePicker } from './useActivityDatePicker.hook';
import { useActivityStudent } from './useActivityStudent.hook';
type ActivityModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity?: ActivityFormData & { id: string };
  month?: number;
  year?: number;
  preselectedActivity?: string;
  enableOnlyProvidedMonthAndYear?: boolean;
};

export const ActivityModal = ({
  open,
  onOpenChange,
  activity,
  month,
  year,
  preselectedActivity,
  enableOnlyProvidedMonthAndYear,
}: ActivityModalProps) => {
  const { methods, handleSave, handleCancel, isLoading, isEdit } = useActivityForm({
    onOpenChange,
    activity,
    preselectedActivity,
  });
  const { data: activities = [] } = useStudentActivitiesQuery();
  const { student } = useActivityStudent();
  const { isDateDisabled, initialDate } = useActivityDatePicker({
    student,
    month,
    year,
    enableOnlyProvidedMonthAndYear,
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit activity' : 'Add activity'}
      className="h-full max-h-full w-full md:h-auto lg:w-signature-modal"
    >
      <form onSubmit={handleSave}>
        <div className="flex flex-col gap-4">
          <div>
            <Controller
              name="name"
              control={methods.control}
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                <SelectField
                  label="Activity"
                  options={[
                    { label: 'Select', value: '' },
                    ...activities.map(({ key, value }) => ({ label: value, value: key })),
                  ]}
                  error={error?.message}
                  value={value}
                  onValueChange={onChange}
                  {...field}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="date"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Date"
                  error={error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  isDateDisabled={isDateDisabled}
                  initialDate={initialDate}
                />
              )}
            />
            <Controller
              name="durationInMinutes"
              control={methods.control}
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                <SelectField
                  label="Duration"
                  options={generateDurationOptions()}
                  error={error?.message}
                  value={value?.toString()}
                  onValueChange={(val) => onChange(Number(val))}
                  {...field}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="note"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextAreaField
                  rows={4}
                  placeholder="Type note"
                  error={
                    field.value?.length > 1000
                      ? 'The note must not exceed 1000 characters'
                      : error?.message
                  }
                  maxLength={1000}
                  showCharacterCount
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <ModalFooter className="pb-6">
          <Button
            preset="primary"
            type="submit"
            disabled={!methods.formState.isValid || !methods.formState.isDirty}
            isLoading={isLoading}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
          <Button preset="tertiary" onClick={handleCancel}>
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
